import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Backdrop,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, themeQuartz } from "ag-grid-community";
import { toDisplayName } from "../utils/columnUtils";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from "../config/maintenanceConfig";
import "./../css/agGridCustom.css";

const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";
const PROPOSED_ACTION = "proposed - evaluation in progress";

const kpiColors = {
  totalSO: "#E53935",
  geography: "#43A047",
  skillTower: "#1E88E5",
  pdl: "#8E24AA",
  market: "#FB8C00",
  marketUnit: "#00ACC1",
  hiringManager: "#5E35B1",
  identifiedCount: "#D81B60",
};

const CHART_COLORS = [
  "#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00",
  "#00ACC1", "#5E35B1", "#D81B60", "#FF6384", "#36A2EB",
  "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF"
];

const PIE_COLORS = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF", "#E7E9ED"];
const DOUGHNUT_COLORS = ["#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00", "#00ACC1", "#5E35B1", "#D81B60"];

const KPICard = ({ title, value, color, onClick }) => (
  <Paper
    elevation={3}
    onClick={onClick}
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 100,
      borderTop: `4px solid ${color}`,
      cursor: onClick ? "pointer" : "default",
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: "center" }}>{title}</Typography>
    <Typography variant="h4" sx={{ fontWeight: "bold", color }}>{value}</Typography>
  </Paper>
);

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return percent > 0.05 ? (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

const WordCloudComponent = ({ words }) => {
  const shuffledWords = useMemo(() => (words || []).sort(() => Math.random() - 0.5).slice(0, 50), [words]);
  if (!words || words.length === 0) return <Typography color="text.secondary" sx={{ textAlign: "center", mt: 5 }}>No data available</Typography>;
  const maxCount = Math.max(...words.map(w => w.count));
  const minCount = Math.min(...words.map(w => w.count));
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, justifyContent: "center", alignItems: "center", p: 1, height: 250, overflow: "hidden" }}>
      {shuffledWords.map((word, index) => {
        const normalizedValue = minCount === maxCount ? 0.5 : (word.count - minCount) / (maxCount - minCount);
        const size = 11 + normalizedValue * 20;
        const color = CHART_COLORS[index % CHART_COLORS.length];
        const rotation = index % 5 === 0 ? -5 : index % 7 === 0 ? 5 : 0;
        return (
          <MuiTooltip key={word.text} title={`${word.text}: ${word.count}`} arrow>
            <Typography component="span" sx={{ fontSize: `${size}px`, fontWeight: normalizedValue > 0.5 ? 700 : 400, color, cursor: "pointer", transform: `rotate(${rotation}deg)`, transition: "all 0.2s ease", lineHeight: 1.2, "&:hover": { transform: `scale(1.15) rotate(${rotation}deg)` } }}>
              {word.text}
            </Typography>
          </MuiTooltip>
        );
      })}
    </Box>
  );
};

function DemandDashboard({ data: filteredData = [], columns: parentColumns = [], loading = false }) {
  const [requirementMonthData, setRequirementMonthData] = useState([]);
  const [geographyData, setGeographyData] = useState([]);
  const [skillTowerData, setSkillTowerData] = useState([]);
  const [pdlNameData, setPdlNameData] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [marketUnitData, setMarketUnitData] = useState([]);
  const [eipoData, setEipoData] = useState([]);
  const [accountNameData, setAccountNameData] = useState([]);
  const [fulfilmentPlanData, setFulfilmentPlanData] = useState([]);
  const [supplySourceData, setSupplySourceData] = useState([]);
  const [actionData, setActionData] = useState([]);
  const [demandStatusData, setDemandStatusData] = useState([]);
  const [demandTypeData, setDemandTypeData] = useState([]);
  const [soGradeData, setSoGradeData] = useState([]);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [fulfillmentMonthData, setFulfillmentMonthData] = useState([]);
  const [projectBillabilityTypeData, setProjectBillabilityTypeData] = useState([]);
  const [demandValidityData, setDemandValidityData] = useState([]);
  const [kpis, setKpis] = useState({ totalDemand: 0, indiaDemand: 0, onsiteDemand: 0, proposed: 0, allocated: 0, awip: 0, reskill: 0, othersAction: 0 });

  // Drill-through modal
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const [drillRows, setDrillRows] = useState([]);

  useEffect(() => {
    if (filteredData.length > 0) {
      calculateKPIs(filteredData);
      prepareChartData(filteredData);
    } else {
      setKpis({ totalDemand: 0, indiaDemand: 0, onsiteDemand: 0, proposed: 0, allocated: 0, awip: 0, reskill: 0, othersAction: 0 });
      setRequirementMonthData([]);
      setGeographyData([]);
      setSkillTowerData([]);
      setPdlNameData([]);
      setMarketData([]);
      setMarketUnitData([]);
      setEipoData([]);
      setAccountNameData([]);
      setFulfilmentPlanData([]);
      setSupplySourceData([]);
      setActionData([]);
      setDemandStatusData([]);
      setDemandTypeData([]);
      setSoGradeData([]);
      setWordCloudData([]);
      setFulfillmentMonthData([]);
      setProjectBillabilityTypeData([]);
      setDemandValidityData([]);
    }
  }, [filteredData]);

  const findKey = (obj, searchKey) => {
    if (!obj) return null;
    const lowerSearch = searchKey.toLowerCase().replace(/\s+/g, '');
    for (const key of Object.keys(obj)) {
      if (key.toLowerCase().replace(/\s+/g, '') === lowerSearch) return key;
    }
    return null;
  };

  const calculateKPIs = (data) => {
    if (!data || data.length === 0) return;
    const sampleRow = data[0];
    const geoKey = findKey(sampleRow, "Geography");
    const offOnKey = findKey(sampleRow, "Off Onsite") || findKey(sampleRow, "OffOnsite") || findKey(sampleRow, "Off On");
    const actionKey = findKey(sampleRow, "Action");

    const indiaDemand = geoKey ? data.filter(d => String(d[geoKey] || '').toLowerCase() === 'india').length : 0;
    const onsiteDemand = offOnKey ? data.filter(d => String(d[offOnKey] || '').toLowerCase().includes('onsite')).length : 0;

    // Action-based counts (Others includes blank Action so totals always match)
    let proposed = 0, allocated = 0, awip = 0, reskill = 0, othersAction = 0;
    data.forEach(d => {
      const val = actionKey ? String(d[actionKey] || '').trim().toLowerCase() : '';
      if (val === PROPOSED_ACTION) proposed++;
      else if (val === 'allocated') allocated++;
      else if (val === 'awip') awip++;
      else if (val === 'reskill') reskill++;
      else othersAction++;
    });

    setKpis({ totalDemand: data.length, indiaDemand, onsiteDemand, proposed, allocated, awip, reskill, othersAction });
  };

  const openDrillThrough = useCallback((title, rows) => {
    setDrillTitle(title);
    setDrillRows(rows);
    setDrillOpen(true);
  }, []);

  const handleChartClick = useCallback((chartField, clickedValue) => {
    if (!filteredData.length) return;
    const sampleRow = filteredData[0];
    const key = findKey(sampleRow, chartField);
    if (!key) return;
    const rows = filteredData.filter(r => String(r[key] || '').trim() === String(clickedValue).trim());
    openDrillThrough(`${chartField}: ${clickedValue} (${rows.length} records)`, rows);
  }, [filteredData, openDrillThrough]);

  const drillColumns = useMemo(() => {
    if (parentColumns.length > 0) {
      return parentColumns.filter(c => c.field !== "item_id" && c.field !== "actions").map(c => ({
        field: c.field, headerName: c.headerName || toDisplayName(c.field), minWidth: 120, flex: 1,
      }));
    }
    if (drillRows.length === 0) return [];
    return Object.keys(drillRows[0]).filter(k => k !== "item_id").map(k => ({ field: k, headerName: toDisplayName(k), minWidth: 120, flex: 1 }));
  }, [parentColumns, drillRows]);

  const prepareChartData = (data) => {
    if (!data || data.length === 0) return;
    const sampleRow = data[0];
    const reqMonthKey = findKey(sampleRow, "Requirment Month") || findKey(sampleRow, "RequirmentMonth") || findKey(sampleRow, "Requirement Month");
    const geoKey = findKey(sampleRow, "Geography");
    const skillTowerKey = findKey(sampleRow, "Skill Tower") || findKey(sampleRow, "SkillTower");
    const pdlKey = findKey(sampleRow, "PDL Name") || findKey(sampleRow, "PDLName");
    const marketKey = findKey(sampleRow, "Market");
    const marketUnitKey = findKey(sampleRow, "Market Unit") || findKey(sampleRow, "MarketUnit");
    const eipoKey = findKey(sampleRow, "EIPO") || findKey(sampleRow, "Eipo");
    const accountNameKey = findKey(sampleRow, "Account Name") || findKey(sampleRow, "AccountName");
    const fulfilmentPlanKey = findKey(sampleRow, "Fulfilment Plan") || findKey(sampleRow, "FulfilmentPlan");
    const supplySourceKey = findKey(sampleRow, "Supply Source") || findKey(sampleRow, "SupplySource");
    const actionKey = findKey(sampleRow, "Action");
    const demandStatusKey = findKey(sampleRow, "Demand Status") || findKey(sampleRow, "DemandStatus");
    const demandTypeKey = findKey(sampleRow, "Demand Type") || findKey(sampleRow, "DemandType");
    const soGradeKey = findKey(sampleRow, "SO Grade") || findKey(sampleRow, "SOGrade");
    const techSkillKey = findKey(sampleRow, "Technical Skills Required") || findKey(sampleRow, "TechnicalSkillsRequired");
    const primarySkillKey = findKey(sampleRow, "Primary Skill") || findKey(sampleRow, "PrimarySkill");
    const secondarySkillKey = findKey(sampleRow, "Secondary Skill") || findKey(sampleRow, "SecondarySkill");
    const fulfillmentMonthKey = findKey(sampleRow, "Fulfillment Month") || findKey(sampleRow, "FulfillmentMonth") || findKey(sampleRow, "Fulfilment Month");
    const projectBillabilityTypeKey = findKey(sampleRow, "Project Billability Type") || findKey(sampleRow, "ProjectBillabilityType");
    const demandValidityKey = findKey(sampleRow, "Demand Validity") || findKey(sampleRow, "DemandValidity");

    const countByField = (field) => {
      if (!field) return [];
      const counts = {};
      data.forEach(row => { const v = row[field]; if (v && String(v).trim() !== '') counts[v] = (counts[v] || 0) + 1; });
      return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    };

    const reqMonthCounts = countByField(reqMonthKey).sort((a, b) => { if (a.name.toLowerCase() === 'past') return -1; if (b.name.toLowerCase() === 'past') return 1; return b.value - a.value; });
    setRequirementMonthData(reqMonthCounts.slice(0, 12));
    setGeographyData(countByField(geoKey).slice(0, 10));
    setSkillTowerData(countByField(skillTowerKey).slice(0, 10));
    setPdlNameData(countByField(pdlKey).slice(0, 10));
    setMarketData(countByField(marketKey).slice(0, 10));
    setMarketUnitData(countByField(marketUnitKey).slice(0, 10));
    setEipoData(countByField(eipoKey).slice(0, 10));
    setAccountNameData(countByField(accountNameKey).slice(0, 10));
    setFulfilmentPlanData(countByField(fulfilmentPlanKey).slice(0, 10));
    setSupplySourceData(countByField(supplySourceKey).slice(0, 10));
    setActionData(countByField(actionKey).slice(0, 10));
    setDemandStatusData(countByField(demandStatusKey).slice(0, 10));
    setDemandTypeData(countByField(demandTypeKey).slice(0, 10));
    setSoGradeData(countByField(soGradeKey).slice(0, 10));

    // Sort Fulfillment Month by date (MMM-YY format) ascending
    const monthOrder = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
    const parseMmmYy = (str) => {
      if (!str) return { year: 9999, month: 99 };
      const parts = String(str).trim().toLowerCase().split('-');
      if (parts.length !== 2) return { year: 9999, month: 99 };
      const mon = monthOrder[parts[0].substring(0, 3)];
      const yr = parseInt(parts[1], 10);
      const fullYear = yr < 50 ? 2000 + yr : 1900 + yr;
      return { year: fullYear, month: mon !== undefined ? mon : 99 };
    };
    const fulfillmentMonthCounts = countByField(fulfillmentMonthKey).sort((a, b) => {
      const pa = parseMmmYy(a.name);
      const pb = parseMmmYy(b.name);
      if (pa.year !== pb.year) return pa.year - pb.year;
      return pa.month - pb.month;
    });
    setFulfillmentMonthData(fulfillmentMonthCounts.slice(0, 12));
    setProjectBillabilityTypeData(countByField(projectBillabilityTypeKey).slice(0, 10));
    setDemandValidityData(countByField(demandValidityKey).slice(0, 10));

    // Word Cloud
    const skillWords = {};
    data.forEach(row => {
      if (techSkillKey && row[techSkillKey]) String(row[techSkillKey]).split(/[,;|\/]+/).forEach(s => { const t = s.trim().toLowerCase(); if (t && t.length > 2) skillWords[t] = (skillWords[t] || 0) + 1; });
      if (primarySkillKey && row[primarySkillKey]) { const t = String(row[primarySkillKey]).trim().toLowerCase(); if (t && t.length > 2) skillWords[t] = (skillWords[t] || 0) + 1; }
      if (secondarySkillKey && row[secondarySkillKey]) String(row[secondarySkillKey]).split(/[,;|\/]+/).forEach(s => { const t = s.trim().toLowerCase(); if (t && t.length > 2) skillWords[t] = (skillWords[t] || 0) + 1; });
    });
    setWordCloudData(Object.entries(skillWords).map(([text, count]) => ({ text, count })).sort((a, b) => b.count - a.count).slice(0, 50));
  };

  const handleBarClick = (chartField) => (data) => { if (data && data.name) handleChartClick(chartField, data.name); };
  const handlePieClick = (chartField) => (data) => { if (data && data.name) handleChartClick(chartField, data.name); };

  return (
    <Box sx={{ p: 0 }}>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit">Loading Dashboard...</Typography>
        </Box>
      </Backdrop>

      {/* KPI Cards - Grouped: Demand Overview (3) + Action Based (5) */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Group 1: Demand Overview */}
        <Grid item xs={12} lg={4.5}>
          <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#f5f9ff', border: '1px solid #bbdefb', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: primaryColor, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Demand Overview</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={4}><KPICard title="Total Demand" value={kpis.totalDemand} color={kpiColors.totalSO} onClick={() => openDrillThrough(`All Records (${kpis.totalDemand})`, filteredData)} /></Grid>
              <Grid item xs={4}><KPICard title="India Demand" value={kpis.indiaDemand} color={kpiColors.geography} onClick={() => { const gKey = findKey(filteredData[0], "Geography"); if (gKey) openDrillThrough(`India Demand (${kpis.indiaDemand})`, filteredData.filter(d => String(d[gKey] || '').toLowerCase() === 'india')); }} /></Grid>
              <Grid item xs={4}><KPICard title="Onsite Demand" value={kpis.onsiteDemand} color={kpiColors.skillTower} onClick={() => { const oKey = findKey(filteredData[0], "Off Onsite") || findKey(filteredData[0], "OffOnsite") || findKey(filteredData[0], "Off On"); if (oKey) openDrillThrough(`Onsite Demand (${kpis.onsiteDemand})`, filteredData.filter(d => String(d[oKey] || '').toLowerCase().includes('onsite'))); }} /></Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Group 2: Action Based */}
        <Grid item xs={12} lg={7.5}>
          <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#fefcf5', border: '1px solid #ffe0b2', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#E65100', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>Action Based</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={2.4}><KPICard title="Proposed" value={kpis.proposed} color="#FB8C00" onClick={() => { const aKey = findKey(filteredData[0], "Action"); if (aKey) openDrillThrough(`Action: Proposed - Evaluation in Progress (${kpis.proposed})`, filteredData.filter(d => String(d[aKey] || '').trim().toLowerCase() === PROPOSED_ACTION)); }} /></Grid>
              <Grid item xs={2.4}><KPICard title="Allocated" value={kpis.allocated} color="#43A047" onClick={() => { const aKey = findKey(filteredData[0], "Action"); if (aKey) openDrillThrough(`Action: Allocated (${kpis.allocated})`, filteredData.filter(d => String(d[aKey] || '').toLowerCase() === 'allocated')); }} /></Grid>
              <Grid item xs={2.4}><KPICard title="AWIP" value={kpis.awip} color="#1E88E5" onClick={() => { const aKey = findKey(filteredData[0], "Action"); if (aKey) openDrillThrough(`Action: AWIP (${kpis.awip})`, filteredData.filter(d => String(d[aKey] || '').toLowerCase() === 'awip')); }} /></Grid>
              <Grid item xs={2.4}><KPICard title="Reskill" value={kpis.reskill} color="#00ACC1" onClick={() => { const aKey = findKey(filteredData[0], "Action"); if (aKey) openDrillThrough(`Action: Reskill (${kpis.reskill})`, filteredData.filter(d => String(d[aKey] || '').toLowerCase() === 'reskill')); }} /></Grid>
              <Grid item xs={2.4}><KPICard title="Others" value={kpis.othersAction} color="#8E24AA" onClick={() => { const aKey = findKey(filteredData[0], "Action"); if (aKey) openDrillThrough(`Action: Others (${kpis.othersAction})`, filteredData.filter(d => { const v = String(d[aKey] || '').trim().toLowerCase(); return v !== PROPOSED_ACTION && v !== 'allocated' && v !== 'awip' && v !== 'reskill'; })); }} /></Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 1: Geography, Project Billability Type, Fulfillment Month */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Geography Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={geographyData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Geography")} cursor="pointer">
                {geographyData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Project Billability Type</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={projectBillabilityTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Project Billability Type")} cursor="pointer">
                {projectBillabilityTypeData.map((e, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Fulfillment Month Wise Count</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={fulfillmentMonthData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Fulfillment Month")} cursor="pointer">
                  {fulfillmentMonthData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 2: Demand Validity, Fulfilment Plan, PDL Name */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Demand Validity</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={demandValidityData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Demand Validity")} cursor="pointer">
                {demandValidityData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Fulfilment Plan Wise</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={fulfilmentPlanData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Fulfilment Plan")} cursor="pointer">
                  {fulfilmentPlanData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>PDL Name Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={pdlNameData} cx="50%" cy="50%" outerRadius={90} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("PDL Name")} cursor="pointer">
                {pdlNameData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 3: Skill Tower, Requirement Month, Market */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Skill Tower Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={skillTowerData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Skill Tower")} cursor="pointer">
                  {skillTowerData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Requirement Month Wise Count</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={requirementMonthData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Requirment Month")} cursor="pointer">
                  {requirementMonthData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Market Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={marketData} cx="50%" cy="50%" outerRadius={90} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Market")} cursor="pointer">
                {marketData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 4: Market Unit, EI/PO, Top 10 Accounts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Market Unit Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={marketUnitData} cx="50%" cy="50%" outerRadius={90} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Market Unit")} cursor="pointer">
                {marketUnitData.map((e, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>EI/PO Distribution</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={eipoData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("EIPO")} cursor="pointer">
                {eipoData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Top 10 Accounts</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={accountNameData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis dataKey="name" type="category" width={75} fontSize={9} /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Account Name")} cursor="pointer">
                  {accountNameData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 5: Supply Source, Action, Demand Status */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Supply Source Wise</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={supplySourceData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Supply Source")} cursor="pointer">
                  {supplySourceData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Action Wise</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={actionData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Action")} cursor="pointer">
                {actionData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Demand Status</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={demandStatusData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("Demand Status")} cursor="pointer">
                  {demandStatusData.map((e, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Chart Row 6: Demand Type, Demanding Skills, SO Grade Wise */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Demand Type</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={demandTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" labelLine={false} label={renderCustomizedLabel} onClick={handlePieClick("Demand Type")} cursor="pointer">
                {demandTypeData.map((e, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
              </Pie><Tooltip /><Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} /></PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>Demanding Skills</Typography>
            <WordCloudComponent words={wordCloudData} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>SO Grade Wise Requirement</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={soGradeData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} fontSize={10} /><YAxis /><Tooltip />
                <Bar dataKey="value" name="Count" onClick={handleBarClick("SO Grade")} cursor="pointer">
                  {soGradeData.map((e, i) => (<Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Drill-through Modal */}
      <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: primaryColor, color: "#fff" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>{drillTitle}</Typography>
          <IconButton onClick={() => setDrillOpen(false)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 2, pb: 2, height: "70vh" }}>
          <div className="ag-custom" style={{
            "--primary-color": primaryColor,
            "--dark-grey": darkGrey,
            height: "100%",
            width: "100%",
            marginTop: 8
          }}>
            <AgGridReact
              theme={themeQuartz}
              modules={[AllCommunityModule]}
              rowData={drillRows}
              columnDefs={drillColumns}
              defaultColDef={{
                sortable: true, filter: true, resizable: true, minWidth: 150,
                tooltipValueGetter: (params) => params.value != null ? String(params.value) : '',
                comparator: (valueA, valueB) => { const a = Number(valueA); const b = Number(valueB); if (!isNaN(a) && !isNaN(b)) return a - b; return String(valueA || '').localeCompare(String(valueB || '')); },
              }}
              rowHeight={42}
              headerHeight={75}
              rowClassRules={{
                'fp-proposed': (params) => {
                  const fp = String(params.data?.["Fulfilment Plan"] || "").toLowerCase();
                  return fp.includes("proposed");
                },
                'assigned-row': (params) => {
                  const row = params.data || {};
                  const fp = String(row["Fulfilment Plan"] || "").toLowerCase();
                  if (fp.includes("proposed")) return false;
                  if (fp.includes("selected") || fp.includes("allocated") || fp.includes("awip")) return true;
                  const empId = row["Employee Id"] || row["EmployeeId"] || "";
                  const empName = row["Employee Name"] || row["employeeName"] || "";
                  return (empId && String(empId).trim() !== "") || (empName && String(empName).trim() !== "");
                },
              }}
              pagination={true}
              paginationPageSize={DEFAULT_PAGE_SIZE}
              paginationPageSizeSelector={DEFAULT_PAGE_SIZE_OPTIONS}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              getRowId={(params) => String(params.data.item_id || params.data.id || Math.random())}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillOpen(false)} variant="contained" sx={{ bgcolor: primaryColor }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DemandDashboard;
